import React, { useState, useEffect } from 'react';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { GetFilterUrl, showToastNotification } from '@/utility/helper';
import { IOrder, IOrderResponse } from '@/interfaces/Order/order.interface';
import { IDrivers, IAvailableDriversResponse, DriverStatus } from '@/interfaces/Driver/driver.interface';
import { MultiSelect } from '@/components/common/MultiSelect';
import { Truck } from 'lucide-react';

interface MultipleOrdersDriverAssignmentProps {
    onClose?: () => void;
}

export default function MultipleOrdersDriverAssignment({ onClose }: MultipleOrdersDriverAssignmentProps) {
    // Live Order & Driver states loaded from API
    const [orders, setOrders] = useState<IOrder[]>([]);
    const [loadingOrders, setLoadingOrders] = useState(false);

    const [drivers, setDrivers] = useState<IDrivers[]>([]);
    const [loadingDrivers, setLoadingDrivers] = useState(false);

    // Selected Targets (Supports Multiple Orders for Single Driver assignment)
    const [selectedOrders, setSelectedOrders] = useState<IOrder[]>([]);
    const [selectedDriver, setSelectedDriver] = useState<IDrivers | null>(null);

    const [assignLoading, setAssignLoading] = useState(false);

    // ── Fetch Pending Orders from API ──────────────────────────────────────
    const fetchOrders = async (searchVal?: string) => {
        setLoadingOrders(true);
        try {
            const queryParams = GetFilterUrl({
                status: 'in_progress',
                limit: 200,
                search: searchVal || ''
            });
            const response = await callApi<IOrderResponse>(
                `${API_URL}${API_ENDPOINT.orders}?${queryParams}`,
                { method: 'GET' }
            );
            if (response?.data?.order) {
                setOrders(response.data.order);
            }
        } catch (error) {
            console.error("Error fetching orders:", error);
        } finally {
            setLoadingOrders(false);
        }
    };

    // ── Fetch Available Drivers from API ───────────────────────────────────
    const fetchDrivers = async (query = '') => {
        setLoadingDrivers(true);
        try {
            const queryParams = new URLSearchParams();
            if (query) queryParams.append('search', query);
            queryParams.append('limit', '100');

            const response = await callApi<IAvailableDriversResponse>(
                `${API_URL}${API_ENDPOINT.drivers}/available?${queryParams.toString()}`,
                { method: 'GET' }
            );
            
            let incoming: IDrivers[] = [];
            if (Array.isArray(response?.users)) {
                incoming = response.users;
            } else if (Array.isArray(response?.data)) {
                incoming = response.data;
            } else if (response?.data && typeof response.data === 'object' && 'users' in response.data) {
                incoming = Array.isArray(response.data.users) ? response.data.users : [];
            }
            setDrivers(incoming);
        } catch (error) {
            console.error('Failed to fetch drivers', error);
        } finally {
            setLoadingDrivers(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchOrders();
        fetchDrivers();
    }, []);

    // ── Handlers ────────────────────────────────────────────────────────────
    const handleConfirmAssignment = async () => {
        if (selectedOrders.length === 0 || !selectedDriver) {
            showToastNotification('danger', 'Please select order(s) and a driver first');
            return;
        }

        setAssignLoading(true);
        try {
            // Assign each selected order to the driver in sequence
            const orderIds = selectedOrders.map(order => order.id);
            const payload = {
                orderIds: orderIds,
                driverId: selectedDriver.id,
                status: 'assigned',
            };
            await callApi(
                `${API_URL}${API_ENDPOINT.assignDriver}`,
                { method: 'PATCH', data: payload }
            );

            showToastNotification('success', 'Driver assigned successfully');

            // Clean selection
            setSelectedOrders([]);
            setSelectedDriver(null);

            // Close the modal
            if (onClose) onClose();
        } catch (error) {
            console.error('Failed to assign driver', error);
            try {
                ThrowError(error);
            } catch (err) {
                // error handled
            }
        } finally {
            setAssignLoading(false);
        }
    };

    return (
        <div className="max-w-xl mx-auto space-y-5 pb-4 text-slate-800 text-left animate-in fade-in duration-300">
            <p className="text-xs text-slate-500">
                Assign multiple ready-for-pickup orders to a single available driver.
            </p>

            {/* Form Fields */}
            <div className="space-y-5">

                {/* Order Selector using common MultiSelect component */}
                <div className="space-y-2 text-left">
                    <label className="text-xs font-bold text-slate-550 uppercase tracking-wider block">
                        Select Orders
                    </label>
                    <MultiSelect
                        options={orders.map(o => ({
                            label: `#${o.orderId || o.id} - ${o.user?.fullName || 'Guest'} (₹${o.finalAmount})`,
                            value: o.id,
                        }))}
                        selected={selectedOrders.map(o => o.id)}
                        onChange={(ids) => setSelectedOrders(orders.filter(o => ids.includes(o.id)))}
                        onSearchChange={fetchOrders}
                        placeholder="Choose ready-for-pickup orders..."
                        loading={loadingOrders}
                    />
                </div>

                {/* Driver Selector using common MultiSelect component with singleSelect */}
                <div className="space-y-2 text-left">
                    <label className="text-xs font-bold text-slate-555 uppercase tracking-wider block">
                        Select Driver
                    </label>
                    <MultiSelect
                        options={drivers.map(d => {
                            const status = d.driverInfo?.driverStatus;
                            let statusText = '[Available]';
                            let statusColor = '#10b981'; // emerald-500

                            if (status === DriverStatus.ON_RIDE) {
                                statusText = '[On Ride]';
                                statusColor = '#f59e0b'; // amber-500
                            } else if (status === DriverStatus.ON_DELIVERY) {
                                statusText = '[On Delivery]';
                                statusColor = '#3b82f6'; // blue-500
                            } else if (status === DriverStatus.AVAILABLE) {
                                statusText = '[Available]';
                                statusColor = '#10b981'; // emerald-500
                            } else if (d.isOnride) {
                                // fallback for legacy
                                statusText = '[On Ride]';
                                statusColor = '#f59e0b';
                            }

                            return {
                                label: `${d.fullName} (${d.phone}) ${statusText}`,
                                value: d.id,
                                color: statusColor,
                            };
                        })}
                        selected={selectedDriver ? [selectedDriver.id] : []}
                        onChange={(ids) => setSelectedDriver(drivers.find(d => d.id === ids[0]) || null)}
                        onSearchChange={fetchDrivers}
                        placeholder="Choose an available driver..."
                        loading={loadingDrivers}
                        singleSelect={true}
                    />
                </div>

            </div>

            {/* Confirm Assignment Action Button */}
            <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100">
                {onClose && (
                    <button
                        onClick={onClose}
                        className="px-4 py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-600 font-bold text-xs rounded-xl shadow-sm transition-all cursor-pointer"
                    >
                        Cancel
                    </button>
                )}
                <button
                    onClick={handleConfirmAssignment}
                    disabled={selectedOrders.length === 0 || !selectedDriver || assignLoading}
                    className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${selectedOrders.length === 0 || !selectedDriver || assignLoading
                        ? 'bg-slate-100 text-slate-450 border border-slate-200 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white shadow-sm shadow-green-600/10 active:scale-98'
                        }`}
                >
                    <Truck className="w-4 h-4" />
                    {assignLoading ? 'Assigning...' : `Assign ${selectedOrders.length} Orders`}
                </button>
            </div>
        </div>
    );
}
